/**
 * DeformationHeightmap — single-channel persistent depression buffer.
 *
 * Stores per-cell depression depth as Float32 on the CPU, packed to RGBA16F
 * on the GPU. The R channel of the GPU texture is the only meaningful one
 * (B/G/A reserved for v2 layered-material data).
 *
 * Why R only and not the GDC 2014 R+G two-channel split (min vs additive)?
 * In v1 we use a single accumulator with `min(current, newDepth)` to enforce
 * "the deepest agent wins" — equivalent to the GDC scheme without needing
 * the additive channel. v2 will add the additive channel for boots packing
 * snow further than the surface displacement allowed.
 */

import * as THREE from 'three/webgpu';
import type { TerrainDeformConfig, TerrainDeformer } from './TerrainDeformConfig.js';

export interface DeformationHeightmapStats {
  /** Number of deformers applied this frame. */
  deformersThisFrame: number;
  /** Sum of depression across all cells (gross deformation indicator). */
  totalDepression: number;
  /** Max depression of any cell (clipping indicator vs `maxDepth`). */
  maxDepression: number;
  /** Wall-clock ms for last upload. */
  updateTimeMs: number;
}

export class DeformationHeightmap {
  readonly config: TerrainDeformConfig;
  readonly N: number;

  /** Per-cell depression depth (positive = depressed). */
  readonly depth: Float32Array;
  /** GPU texture (RGBA16F, half-float). R channel = depth. */
  readonly texture: THREE.DataTexture;

  private _gpuBuffer: Uint16Array;
  /**
   * Bumped by every mutation that can change a cell (a deposit, a refill or relax that lowered one, a scroll, a
   * clear). A consumer that mirrors the field (a GPU copy, a render-target bake) re-uploads only when it moved —
   * `upload()` packs N² halves, which is not a per-frame cost at 512² (weather surfaces, 2026-09-30).
   */
  version = 0;
  private _stats: DeformationHeightmapStats = {
    deformersThisFrame: 0, totalDepression: 0, maxDepression: 0, updateTimeMs: 0,
  };

  constructor(config: TerrainDeformConfig) {
    this.config = config;
    this.N = config.quality.resolution;
    this.depth = new Float32Array(this.N * this.N);
    this._gpuBuffer = new Uint16Array(this.N * this.N * 4); // RGBA half-float
    this.texture = new THREE.DataTexture(
      this._gpuBuffer, this.N, this.N,
      THREE.RGBAFormat, THREE.HalfFloatType,
    );
    this.texture.minFilter = THREE.LinearFilter;
    this.texture.magFilter = THREE.LinearFilter;
    this.texture.wrapS = THREE.ClampToEdgeWrapping;
    this.texture.wrapT = THREE.ClampToEdgeWrapping;
    this.texture.needsUpdate = true;
  }

  get stats(): Readonly<DeformationHeightmapStats> { return this._stats; }

  /** Reset agent counter (call once per frame before depositing). */
  beginFrame(): void {
    this._stats.deformersThisFrame = 0;
  }

  /**
   * Apply slow refill — moves all cells toward 0 by `refillRate * dt`
   * per second. With `refillRate = 0` (default), this is a no-op.
   */
  refill(dt: number): void {
    const r = this.config.refillRate;
    if (r <= 0) return;
    this.relax(r * dt);
  }

  /**
   * Lower every depressed cell by `amount` metres (never below 0). The refill
   * with an explicit amount: fresh snowfall filling the tracks at the weather's
   * rate, mud settling under rain — GDC 2014's "subtract a small value to make
   * snow gradually replenish (since it's snowing)". Returns whether any cell
   * changed (and bumps `version` only then).
   */
  relax(amount: number): boolean {
    if (!(amount > 0)) return false;
    let changed = false;
    const d = this.depth;
    for (let i = 0; i < d.length; i++) {
      const v = d[i];
      if (v > 0) { d[i] = v > amount ? v - amount : 0; changed = true; }
    }
    if (changed) this.version++;
    return changed;
  }

  /**
   * THE CAMERA-FOLLOW WINDOW (the v2 the config promised): slide the bounds by
   * whole cells — (+di, +dj) moves the window toward +x / +z — and shift the
   * data with them, so a print keeps its WORLD position while the window
   * follows the player; cells that enter the window start undeformed and
   * cells that leave it are forgotten (nothing persists past the window).
   * Whole cells only, so no resampling blur ever accumulates.
   */
  scroll(di: number, dj: number): void {
    di = Math.trunc(di); dj = Math.trunc(dj);
    if (di === 0 && dj === 0) return;
    const N = this.N;
    const b = this.config.bounds;
    const sx = (b.maxX - b.minX) / (N - 1);
    const sz = (b.maxZ - b.minZ) / (N - 1);
    b.minX += di * sx; b.maxX += di * sx;
    b.minZ += dj * sz; b.maxZ += dj * sz;
    const d = this.depth;
    if (Math.abs(di) >= N || Math.abs(dj) >= N) { d.fill(0); this.version++; return; }
    // Walk so that a cell is read before it is overwritten: rows toward the shift's source first.
    const j0 = dj >= 0 ? 0 : N - 1, j1 = dj >= 0 ? N : -1, js = dj >= 0 ? 1 : -1;
    const i0 = di >= 0 ? 0 : N - 1, i1 = di >= 0 ? N : -1, is = di >= 0 ? 1 : -1;
    for (let j = j0; j !== j1; j += js) {
      const sj = j + dj;
      for (let i = i0; i !== i1; i += is) {
        const si = i + di;
        d[j * N + i] = sj >= 0 && sj < N && si >= 0 && si < N ? d[sj * N + si] : 0;
      }
    }
    this.version++;
  }

  /**
   * Deposit a deformer footprint. Uses `max(currentDepth, newDepth)` per
   * cell so the deepest agent wins rather than accumulating linearly —
   * matches the GDC 2014 "depth-min" pattern (we store positive depth, so
   * the operator is `max`).
   */
  deposit(d: TerrainDeformer): void {
    const N = this.N;
    const b = this.config.bounds;
    // Use (N-1) spacing to match sampleBilinear's grid mapping: N samples span
    // the bounds inclusively, so the cell size is range/(N-1). Using /N left
    // deposits offset by ~0.5 cell from where queries read them.
    const sx = (b.maxX - b.minX) / (N - 1);
    const sz = (b.maxZ - b.minZ) / (N - 1);
    const cellSize = Math.min(sx, sz);
    const cap = this.config.maxDepth;

    if (d.kind === 'sphere') {
      const gridR = d.radius / cellSize;
      const gridR2 = gridR * gridR;
      const sigma2 = Math.max(0.25, gridR2);
      const gx = (d.x - b.minX) / sx;
      const gz = (d.z - b.minZ) / sz;
      this._depositCircle(gx, gz, gridR, gridR2, sigma2, d.depth, cap);
    } else {
      // Capsule: walk segment, depositing circles at sample points.
      // Sample density: one circle per (cellSize) along the segment.
      const dx = d.x1 - d.x0;
      const dz = d.z1 - d.z0;
      const segLen = Math.sqrt(dx * dx + dz * dz);
      const samples = Math.max(1, Math.ceil(segLen / cellSize));
      const gridR = d.radius / cellSize;
      const gridR2 = gridR * gridR;
      const sigma2 = Math.max(0.25, gridR2);
      for (let s = 0; s <= samples; s++) {
        const t = samples === 0 ? 0 : s / samples;
        const wx = d.x0 + dx * t;
        const wz = d.z0 + dz * t;
        const gx = (wx - b.minX) / sx;
        const gz = (wz - b.minZ) / sz;
        this._depositCircle(gx, gz, gridR, gridR2, sigma2, d.depth, cap);
      }
    }
    this._stats.deformersThisFrame++;
  }

  private _depositCircle(
    gx: number, gz: number,
    gridR: number, gridR2: number, sigma2: number,
    depth: number, cap: number,
  ): void {
    const N = this.N;
    const i0 = Math.max(0, Math.floor(gx - gridR));
    const i1 = Math.min(N - 1, Math.ceil(gx + gridR));
    const j0 = Math.max(0, Math.floor(gz - gridR));
    const j1 = Math.min(N - 1, Math.ceil(gz + gridR));
    for (let j = j0; j <= j1; j++) {
      for (let i = i0; i <= i1; i++) {
        const dx = i - gx;
        const dz = j - gz;
        const r2 = dx * dx + dz * dz;
        if (r2 > gridR2) continue;
        const f = Math.exp(-r2 / sigma2);
        const newDepth = Math.min(cap, depth * f);
        const cell = j * N + i;
        if (newDepth > this.depth[cell]) { this.depth[cell] = newDepth; this.version++; }
      }
    }
  }

  /** Pack the Float32 depth buffer into the half-float GPU texture. */
  upload(): void {
    const t0 = (typeof performance !== 'undefined') ? performance.now() : Date.now();
    const N = this.N;
    let total = 0;
    let max = 0;
    for (let i = 0; i < N * N; i++) {
      const d = this.depth[i];
      if (d > max) max = d;
      total += d;
      this._gpuBuffer[i * 4] = floatToHalf(d);
      this._gpuBuffer[i * 4 + 1] = 0;
      this._gpuBuffer[i * 4 + 2] = 0;
      this._gpuBuffer[i * 4 + 3] = 0;
    }
    this.texture.needsUpdate = true;
    this._stats.totalDepression = total;
    this._stats.maxDepression = max;
    const t1 = (typeof performance !== 'undefined') ? performance.now() : Date.now();
    this._stats.updateTimeMs = t1 - t0;
  }

  /** Sample depression at world-space XZ. Bilinear. Returns 0 outside bounds. */
  sampleBilinear(worldX: number, worldZ: number): number {
    const N = this.N;
    const b = this.config.bounds;
    const gx = (worldX - b.minX) / (b.maxX - b.minX) * (N - 1);
    const gz = (worldZ - b.minZ) / (b.maxZ - b.minZ) * (N - 1);
    if (gx < 0 || gz < 0 || gx > N - 1 || gz > N - 1) return 0;
    const i0 = Math.floor(gx);
    const j0 = Math.floor(gz);
    const i1 = Math.min(N - 1, i0 + 1);
    const j1 = Math.min(N - 1, j0 + 1);
    const fx = gx - i0;
    const fz = gz - j0;
    const c00 = this.depth[j0 * N + i0];
    const c10 = this.depth[j0 * N + i1];
    const c01 = this.depth[j1 * N + i0];
    const c11 = this.depth[j1 * N + i1];
    return (c00 * (1 - fx) + c10 * fx) * (1 - fz)
         + (c01 * (1 - fx) + c11 * fx) * fz;
  }

  /** Reset the entire heightmap to undeformed. */
  clear(): void {
    this.depth.fill(0);
    this.version++;
    this.texture.needsUpdate = true;
  }

  dispose(): void {
    this.texture.dispose();
  }
}

// IEEE 754 single → half-precision float (shared with InfluenceMap / SmokeTextureSync).
function floatToHalf(val: number): number {
  if (!Number.isFinite(val)) {
    if (Number.isNaN(val)) return 0x7e00;
    return val > 0 ? 0x7c00 : 0xfc00;
  }
  if (val === 0) return 0;
  _f32[0] = val;
  const x = _i32[0];
  const sign = (x >> 16) & 0x8000;
  const exp = ((x >> 23) & 0xff) - (127 - 15);
  const mant = (x & 0x7fffff);
  if (exp <= 0) {
    if (exp < -10) return sign;
    const shifted = (mant | 0x800000) >> (1 - exp);
    return sign | (shifted >> 13);
  } else if (exp >= 31) {
    return sign | 0x7c00;
  }
  return sign | (exp << 10) | (mant >> 13);
}

const _f32 = new Float32Array(1);
const _i32 = new Int32Array(_f32.buffer);
